/**
 * Builds the storefront pathname that matches the preferred locale (cookie / user choice).
 *
 * - Homepage uses `/` when the preferred locale equals `defaultLocale`; otherwise `/${locale}`.
 * - All other routes use `/${locale}${rest}` where `rest` is the path after an optional locale prefix.
 */

export const parsePathLocale = (
  pathname: string,
  supportedLower: Set<string>,
): { localePrefix: string | null; restPath: string } => {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase() ?? '';
  if (first && supportedLower.has(first)) {
    const rest = segments.slice(1);
    return { localePrefix: first, restPath: rest.length ? `/${rest.join('/')}` : '' };
  }
  return {
    localePrefix: null,
    restPath: pathname === '/' ? '' : `/${segments.join('/')}`,
  };
};

export const buildPathnameForPreferredLocale = (
  pathname: string,
  preferredLocale: string,
  defaultLocale: string,
  supportedCodes: string[],
): string => {
  const supported = new Set(supportedCodes.map(c => c.toLowerCase()));
  const pref = preferredLocale.toLowerCase();
  const def = defaultLocale.toLowerCase();

  const { restPath } = parsePathLocale(pathname, supported);

  if (restPath === '') {
    return pref === def ? '/' : `/${pref}`;
  }

  return `/${pref}${restPath}`;
};
